import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { materialService, storageService } from '../services/supabase';
import { FiSave, FiX, FiUpload, FiArrowLeft, FiEye, FiCheckCircle, FiImage } from 'react-icons/fi';
import { normalizeImageUrl } from '../utils/imageUtils';

interface MaterialFormData {
  titulo: string;
  descricao: string;
  tipo: 'vídeo' | 'livro' | 'revista';
  iframe: string;
  thumb_url: string;
}

const MaterialForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<MaterialFormData>({
    titulo: '',
    descricao: '',
    tipo: 'vídeo',
    iframe: '',
    thumb_url: ''
  });
  
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [showIframePreview, setShowIframePreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Use useRef para rastrear a URL de imagem atual de forma estável
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Efeito para atualizar a URL da imagem quando o thumbnail for carregado
  useEffect(() => {
    if (thumbnailPreview && imageRef.current) {
      // Força atualização da imagem, para evitar problemas de cache
      imageRef.current.src = thumbnailPreview;
    }
  }, [thumbnailPreview]);

  useEffect(() => {
    if (isEditMode) {
      fetchMaterial();
    }
  }, [id, isEditMode]);

  const fetchMaterial = async () => {
    try {
      setFetchLoading(true);
      const { data, error } = await materialService.getMaterialById(id!);
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (!data) {
        throw new Error('Material não encontrado');
      }
      
      setFormData({
        titulo: data.titulo,
        descricao: data.descricao,
        tipo: data.tipo,
        iframe: data.iframe,
        thumb_url: data.thumb_url || ''
      });
      
      if (data.thumb_url) {
        setThumbnailPreview(data.thumb_url);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados do material');
      console.error('Erro ao buscar material:', err);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    // Verifica o tamanho do arquivo (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. O tamanho máximo é 5MB.');
      return;
    }
    
    // Verifica se é uma imagem
    if (!file.type.includes('image/')) {
      setError('Por favor, selecione um arquivo de imagem válido.');
      return;
    }
    
    setError(null);
    setUploadSuccess(false);
    setIsUploading(true);
    setThumbnailFile(file);
    
    // Mostrar a pré-visualização imediata usando FileReader
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        // Define diretamente o resultado do FileReader como preview
        const dataUrl = event.target.result as string;
        console.log("Preview atualizado via FileReader");
        setThumbnailPreview(dataUrl);
      }
    };
    reader.readAsDataURL(file);
    
    // Agora fazer o upload para o servidor
    try {
      const materialId = isEditMode ? id! : 'temp-' + Date.now();
      const { data: uploadData, error: uploadError } = await storageService.uploadThumbnail(file, materialId);
      
      if (uploadError) {
        throw new Error('Erro ao fazer upload da thumbnail: ' + uploadError.message);
      }
      
      console.log("Upload concluído com sucesso, URL do servidor:", uploadData.publicUrl);
      
      // Atualiza o formData com a URL retornada pelo servidor
      setFormData(prev => ({ ...prev, thumb_url: uploadData.publicUrl }));
      
      // Mostra mensagem de sucesso
      setUploadSuccess(true);
      
      // Esconde a mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Erro no upload da thumbnail:', err);
      setError(err.message || 'Erro ao fazer upload da thumbnail');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      let thumbnailUrl = formData.thumb_url;
      
      // Upload da nova thumbnail (se houver)
      if (thumbnailFile) {
        const materialId = isEditMode ? id! : 'temp-' + Date.now(); // ID temporário para novos materiais
        const { data: uploadData, error: uploadError } = await storageService.uploadThumbnail(thumbnailFile, materialId);
        
        if (uploadError) {
          throw new Error('Erro ao fazer upload da thumbnail: ' + uploadError.message);
        }
        
        thumbnailUrl = uploadData.publicUrl;
      }
      
      // Dados do material para envio
      const materialData = {
        ...formData,
        thumb_url: thumbnailUrl
      };
      
      let response;
      
      if (isEditMode) {
        response = await materialService.updateMaterial(id!, materialData);
      } else {
        response = await materialService.createMaterial(materialData);
      }
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      setSuccess(true);
      
      // Redirecionar após sucesso
      setTimeout(() => {
        navigate('/materiais');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar material');
      console.error('Erro ao salvar material:', err);
    } finally {
      setLoading(false);
    }
  };

  // Limpa a string de iframe para evitar problemas de segurança
  const sanitizeIframe = (iframeStr: string): string => {
    // Remove scripts e eventos onload/onerror, mantém apenas src seguras
    return iframeStr
      .replace(/on\w+="[^"]*"/g, '')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  // Sanitiza o iframe e gera uma versão segura para preview
  const getSafeIframe = (): string => {
    const sanitized = sanitizeIframe(formData.iframe);
    // Verificar se é um iframe válido
    if (sanitized.includes('<iframe') && sanitized.includes('src=')) {
      return sanitized;
    }
    return '';
  };

  if (fetchLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-center">
        <Link to="/materiais" className="mr-4 text-gray-400 hover:text-white transition-colors">
          <FiArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway">
          {isEditMode ? 'Editar' : 'Novo'} <span className="text-vermelho-claro">Material</span>
        </h1>
      </div>
      
      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-md p-3 mb-6 flex items-center text-red-200 animate-fade-in">
          <FiX className="mr-2 flex-shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 rounded-md p-3 mb-6 flex items-center text-green-200 animate-fade-in">
          <FiSave className="mr-2 flex-shrink-0 text-green-400" />
          <span>Material {isEditMode ? 'atualizado' : 'criado'} com sucesso! Redirecionando...</span>
        </div>
      )}
      
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coluna da esquerda - Informações do material */}
            <div className="space-y-6">
              <div>
                <label htmlFor="titulo" className="block text-sm font-medium text-gray-300 mb-1">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="titulo"
                  name="titulo"
                  required
                  value={formData.titulo}
                  onChange={handleChange}
                  className="input"
                  placeholder="Título do material"
                />
              </div>
              
              <div>
                <label htmlFor="tipo" className="block text-sm font-medium text-gray-300 mb-1">
                  Tipo de material <span className="text-red-500">*</span>
                </label>
                <select
                  id="tipo"
                  name="tipo"
                  required
                  value={formData.tipo}
                  onChange={handleChange}
                  className="input bg-gray-800"
                >
                  <option value="vídeo">Vídeo</option>
                  <option value="livro">Livro Digital</option>
                  <option value="revista">Revista Digital</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-300 mb-1">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  required
                  value={formData.descricao}
                  onChange={handleChange}
                  className="input min-h-[120px]"
                  placeholder="Descrição detalhada do material"
                />
              </div>
              
              <div>
                <label htmlFor="iframe" className="block text-sm font-medium text-gray-300 mb-1">
                  Código de incorporação (iframe) <span className="text-red-500">*</span>
                  <span className="text-gray-500 text-xs ml-2">(YouTube, Vimeo, FlipHTML5, etc)</span>
                </label>
                <textarea
                  id="iframe"
                  name="iframe"
                  required
                  value={formData.iframe}
                  onChange={handleChange}
                  className="input font-mono text-sm min-h-[120px]"
                  placeholder='<iframe src="https://www.youtube.com/embed/..." ...'
                />
                
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => setShowIframePreview(!showIframePreview)}
                    className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                    disabled={!formData.iframe}
                  >
                    <FiEye className="mr-1" />
                    {showIframePreview ? 'Ocultar Preview' : 'Visualizar Preview'}
                  </button>
                </div>
                
                {showIframePreview && formData.iframe && (
                  <div className="mt-3 border border-gray-700 rounded-md p-3 bg-black">
                    <p className="text-xs text-gray-400 mb-2">Preview do conteúdo incorporado:</p>
                    <div 
                      className="aspect-w-16 aspect-h-9 rounded overflow-hidden"
                      dangerouslySetInnerHTML={{ __html: getSafeIframe() }}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Coluna da direita - Thumbnail */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Thumbnail do material
              </label>
              
              <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-700 rounded-lg">
                {thumbnailPreview ? (
                  <div className="mb-4 w-full">
                    <div className="relative rounded-md overflow-hidden max-w-md mx-auto bg-black/40">
                      <img
                        ref={imageRef}
                        src={thumbnailPreview}
                        alt="Preview do thumbnail"
                        className="w-full object-contain mx-auto"
                        style={{ maxHeight: '220px' }}
                        onError={(e) => {
                          console.error('Erro ao carregar imagem de preview:', e);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          
                          const container = e.currentTarget.parentElement;
                          if (container) {
                            container.innerHTML = `
                              <div class="flex flex-col items-center justify-center p-8 text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                  <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                                <span class="mt-2">Erro ao carregar imagem</span>
                              </div>
                            `;
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setThumbnailPreview(null);
                          setThumbnailFile(null);
                          setFormData(prev => ({ ...prev, thumb_url: '' }));
                        }}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 z-10"
                        title="Remover imagem"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                    
                    {uploadSuccess && (
                      <div className="mt-2 flex items-center justify-center text-green-400 text-sm animate-fade-in">
                        <FiCheckCircle className="mr-1" />
                        Thumbnail enviada com sucesso!
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full max-w-md bg-gray-800 rounded-md flex items-center justify-center mb-4" style={{ height: '180px' }}>
                    <div className="text-gray-400 flex flex-col items-center">
                      <FiImage size={40} />
                      <span className="mt-2 text-center">Sem Thumbnail</span>
                    </div>
                  </div>
                )}
                
                <label htmlFor="thumbnail" className={`btn btn-secondary ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <FiUpload className="mr-2" />
                      {thumbnailPreview ? 'Trocar Thumbnail' : 'Fazer Upload'}
                    </>
                  )}
                </label>
                <input
                  type="file"
                  id="thumbnail"
                  accept="image/*"
                  className="hidden"
                  onChange={handleThumbnailChange}
                  disabled={isUploading}
                />
                
                <p className="mt-2 text-xs text-gray-500">
                  Formatos suportados: JPG, PNG, GIF (max. 5MB)
                </p>

                <p className="mt-4 text-sm text-gray-400">
                  A thumbnail é a imagem que aparecerá no card do material na listagem.
                  Use imagens com proporção 16:9 para melhores resultados.
                </p>
              </div>
            </div>
          </div>
          
          {/* Botões de ação */}
          <div className="mt-8 flex justify-end space-x-4">
            <Link
              to="/materiais"
              className="btn btn-secondary"
            >
              <FiX className="mr-2" />
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              ) : (
                <FiSave className="mr-2" />
              )}
              {isEditMode ? 'Atualizar' : 'Salvar'} Material
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default MaterialForm; 